import type { ProseMirrorDiffLine } from "@ext/git/core/Diff/logic/model/ProseMirrorDiffLine";
import type { EditorView } from "prosemirror-view";

const CHANGED_TAB_CLASS = "diff-tab-changed";
const TABS = '[data-component="tabs"]';

const changePos = (line: ProseMirrorDiffLine): number | null => {
	if (line.type === "deleted") return line.insertAfter;
	if (line.type === "added" || line.type === "modified") return line.pos.from;
	return null;
};

// Only the open tab's content is on screen: its header is the one place a change in another tab shows.
const findChangedCases = (view: EditorView, lines: ProseMirrorDiffLine[]): Set<HTMLElement> => {
	const cases = new Set<HTMLElement>();
	const size = view.state.doc.content.size;
	for (const line of lines) {
		const pos = changePos(line);
		if (pos === null) continue;
		const Pos = view.state.doc.resolve(Math.min(pos, size));
		for (let depth = Pos.depth; depth > 0; depth--) {
			if (Pos.node(depth).type.name !== "tab" || Pos.node(depth - 1).type.name !== "tabs") continue;
			const host = view.nodeDOM(Pos.before(depth - 1));
			if (!(host instanceof HTMLElement)) continue;
			const root = host.matches(TABS) ? host : host.querySelector<HTMLElement>(TABS);
			if (!root) continue;
			const index = Pos.index(depth - 1);
			const header = [...root.querySelectorAll<HTMLElement>(`.case[data-tab-index="${index}"]`)].find(
				(element) => element.closest(TABS) === root,
			);
			if (header) cases.add(header);
		}
	}
	return cases;
};

const markChangedTabs = (view: EditorView, lines: ProseMirrorDiffLine[]) => {
	const changed = findChangedCases(view, lines);
	const marked = [...view.dom.querySelectorAll<HTMLElement>(`.${CHANGED_TAB_CLASS}`)];
	marked.forEach((element) => {
		if (!changed.has(element)) element.classList.remove(CHANGED_TAB_CLASS);
	});
	changed.forEach((element) => element.classList.add(CHANGED_TAB_CLASS));
};

export default markChangedTabs;
